import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { clientApi } from "../../api";
import PasswordInput from "../../components/PasswordInput";

/**
 * Dedicated Reset PIN workflow for the front desk.
 *
 * 1. Scan card or type membership number → fetch member (fast path).
 * 2. Pick a new PIN (3-10 digits), confirm.
 * 3. Submit → `POST /members/:id/reset-pin`.
 *
 * Faster than Search → click member → Reset PIN in detail page.
 */
export default function ResetPin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [member, setMember] = useState(null);
  const [looking, setLooking] = useState(false);
  const [lookError, setLookError] = useState("");

  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetError, setResetError] = useState("");
  const [done, setDone] = useState(false);

  const pinInputRef = useRef(null);

  useEffect(() => {
    if (step === 2 && pinInputRef.current) pinInputRef.current.focus();
  }, [step]);

  async function lookup(e) {
    e?.preventDefault();
    setLookError(""); setLooking(true); setMember(null);
    try {
      const digits = identifier.trim().replace(/\s+/g, "");
      if (!digits) throw new Error("Enter a card or membership number.");
      // Card-number path: 7+ digits → look up by card (live S7000).
      // Member-number path: 1-6 digits → use bundle endpoint.
      if (/^\d{7,}$/.test(digits)) {
        const m = await clientApi.getMemberByCard(digits);
        const mm = m?.member || m;
        setMember({
          membershipId: mm.membershipId != null ? String(mm.membershipId) : null,
          firstName: mm.firstName || "",
          lastName:  mm.lastName  || "",
          cardNumber: digits,
        });
      } else if (/^\d{1,6}$/.test(digits)) {
        const b = await clientApi.getMemberBundle(digits);
        const s = b?.s7000?.member || b?.s7000 || {};
        const c = b?.cache || {};
        const l = b?.local || {};
        const firstCard = (b?.cards && b.cards[0]) || {};
        setMember({
          membershipId: digits,
          firstName: s.firstName || c.firstname   || l.first_name || "",
          lastName:  s.lastName  || c.surname     || l.last_name  || "",
          cardNumber: s.barCode  || firstCard.cardnumber || l.card_number || "",
        });
      } else {
        throw new Error("Enter digits only (card number or membership number).");
      }
      setStep(2);
    } catch (err) {
      setLookError(err.message || "Lookup failed");
    }
    setLooking(false);
  }

  async function submit(e) {
    e.preventDefault();
    setResetError("");
    if (!/^\d{3,10}$/.test(newPin)) return setResetError("PIN must be 3–10 digits.");
    if (newPin !== confirmPin) return setResetError("PINs don't match.");
    setBusy(true);
    try {
      await clientApi.resetMemberPin(member.membershipId, {
        pin: newPin,
        cardNumber: member.cardNumber || "",
      });
      setDone(true);
    } catch (err) {
      setResetError(err.message || "Reset failed");
    }
    setBusy(false);
  }

  function startOver() {
    setStep(1); setIdentifier(""); setMember(null); setNewPin(""); setConfirmPin("");
    setDone(false); setLookError(""); setResetError("");
  }

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Reset PIN</h1>
          <p className="page-subtitle">Fast workflow for members who have forgotten their PIN</p>
        </div>
      </div>

      <div style={{ maxWidth: 560 }}>
        <ProgressBar step={step} />

        {step === 1 && (
          <form className="settings-card" onSubmit={lookup}>
            <div className="settings-card-header">
              <h3>Find the member</h3>
              <p>Scan the card or type a membership number</p>
            </div>
            {lookError && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{lookError}</div>}
            <div className="form-field">
              <label>Card number or membership number <span className="required">*</span></label>
              <input
                autoFocus
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, ""))}
                placeholder="Scan card or type number"
                inputMode="numeric"
                pattern="\d*"
                required
              />
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
              <button type="submit" className="btn btn-primary" disabled={looking}>
                {looking ? "Looking up…" : "Find member"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && member && !done && (
          <form className="settings-card" onSubmit={submit}>
            <div className="settings-card-header">
              <h3>Set a new PIN for {member.firstName} {member.lastName}</h3>
              <p>
                Membership <code>#{member.membershipId}</code>
                {member.cardNumber ? <> · Card <code>{member.cardNumber}</code></> : null}
              </p>
            </div>
            {resetError && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{resetError}</div>}
            <div className="form-field">
              <label>New PIN <span className="required">*</span></label>
              <PasswordInput
                ref={pinInputRef}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                placeholder="3–10 digits"
                maxLength={10}
                inputMode="numeric"
                pattern="\d*"
                required
              />
            </div>
            <div className="form-field">
              <label>Confirm new PIN <span className="required">*</span></label>
              <PasswordInput
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                maxLength={10}
                inputMode="numeric"
                pattern="\d*"
                required
              />
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Resetting…" : "Reset PIN"}
              </button>
              <button type="button" className="btn btn-outline" onClick={startOver}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {done && member && (
          <div className="settings-card">
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--success-bg, #10b98122)", color: "var(--success, #10b981)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="22" height="22"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>PIN reset for {member.firstName} {member.lastName}</div>
                <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Membership <code>#{member.membershipId}</code></div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn btn-primary" onClick={startOver}>Reset another</button>
              <button className="btn btn-outline" onClick={() => navigate(`/client/members/${member.membershipId}`)}>Open member</button>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}

function ProgressBar({ step }) {
  return (
    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
      <span style={{ fontWeight: step >= 1 ? 600 : 400, color: step >= 1 ? "var(--text)" : undefined }}>1. Find member</span>
      <span>›</span>
      <span style={{ fontWeight: step >= 2 ? 600 : 400, color: step >= 2 ? "var(--text)" : undefined }}>2. New PIN</span>
    </div>
  );
}
